import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import EarthGlobe from "../components/EarthGlobe";
import TrafficProof from "../components/TrafficProof";
import { addAttribution } from "../lib";
import { highestPublishedBidCents, MAX_CHECKOUT_CENTS, minimumAcceptedBidCents } from "../site-config";
import type { RankedEntry, SiteProps, Slot } from "../types";
import "../styles/earth.css";

type Props = Pick<SiteProps, "slots" | "state" | "onClaim">;

interface BoardEntry {
  entry: RankedEntry;
  slot: Slot;
}

const money = (cents = 0) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: cents % 100 ? 2 : 0,
  }).format(cents / 100);

function link(raw: string): URL | null {
  try {
    const value = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const url = new URL(value);
    return /^https?:$/.test(url.protocol) ? url : null;
  } catch {
    return null;
  }
}

function outboundLink(raw: string, content: string): string {
  const destination = link(raw);
  if (!destination) return "#";
  return addAttribution(destination.toString(), {
    source: "outbid.earth",
    medium: "referral",
    campaign: "earth_leaderboard",
    content,
  });
}

function favicon(raw: string): string | undefined {
  const destination = link(raw);
  return destination ? new URL("/favicon.ico", destination.origin).toString() : undefined;
}

function timeAgo(raw: string): string {
  const elapsed = Date.now() - new Date(raw).getTime();
  const minutes = Math.max(1, Math.floor(elapsed / 60_000));
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

export default function EarthSite({ slots, state, onClaim }: Props) {
  const highestBidCents = highestPublishedBidCents(state);
  const nextBidCents = minimumAcceptedBidCents(state);
  const biddingClosed = nextBidCents > MAX_CHECKOUT_CENTS;
  const displayedBidCents = Math.min(nextBidCents, MAX_CHECKOUT_CENTS);
  const sharedCountry = new URLSearchParams(window.location.search).get("country");
  const [selectedId, setSelectedId] = useState(
    () => slots.find((slot) => slot.id === sharedCountry)?.id ?? "",
  );
  const [filterId, setFilterId] = useState("all");
  const [url, setUrl] = useState("");
  const [amount, setAmount] = useState(String(displayedBidCents / 100));
  const [targetRank, setTargetRank] = useState(1);
  const [dark, setDark] = useState(false);
  const [formError, setFormError] = useState("");
  const urlInputRef = useRef<HTMLInputElement>(null);

  const selected = slots.find((slot) => slot.id === selectedId);
  const active = useMemo(() => slots.filter((slot) => slot.entries.length), [slots]);
  const countryOptions = useMemo(
    () => [...slots].sort((a, b) => a.label.localeCompare(b.label)),
    [slots],
  );
  const globalEntries = useMemo<BoardEntry[]>(
    () =>
      slots
        .flatMap((slot) => slot.entries.map((entry) => ({ entry, slot })))
        .sort(
          (a, b) =>
            b.entry.amountCents - a.entry.amountCents ||
            a.entry.createdAt.localeCompare(b.entry.createdAt) ||
            a.slot.label.localeCompare(b.slot.label) ||
            a.entry.id.localeCompare(b.entry.id),
        ),
    [slots],
  );
  const displayedEntries = useMemo(
    () => globalEntries.filter(({ slot }) => filterId === "all" || slot.id === filterId).slice(0, 50),
    [filterId, globalEntries],
  );
  const recentEntries = useMemo(
    () => [...globalEntries].sort((a, b) => b.entry.createdAt.localeCompare(a.entry.createdAt)).slice(0, 5),
    [globalEntries],
  );
  const rankByEntry = useMemo(
    () => new Map(globalEntries.map(({ entry, slot }, index) => [`${slot.id}:${entry.id}`, index + 1])),
    [globalEntries],
  );

  useEffect(() => {
    document.documentElement.classList.toggle("earth-theme-dark", dark);
    return () => document.documentElement.classList.remove("earth-theme-dark");
  }, [dark]);

  useEffect(() => {
    const amountCents = Math.round(Number(amount) * 100);
    if (biddingClosed || !Number.isSafeInteger(amountCents) || amountCents < nextBidCents) {
      setAmount(String(displayedBidCents / 100));
    }
  }, [biddingClosed, displayedBidCents, nextBidCents]);

  const selectCountry = (slot?: Slot, updateFilter = false) => {
    if (!slot) return;
    setSelectedId(slot.id);
    setTargetRank(1);
    setAmount(String(displayedBidCents / 100));
    if (updateFilter) setFilterId(slot.id);
    const location = new URL(window.location.href);
    location.searchParams.set("country", slot.id);
    window.history.replaceState(null, "", location);
    setFormError("");
  };

  const changeAmount = (difference: number) => {
    if (biddingClosed) return;
    const current = Number(amount) || displayedBidCents / 100;
    setAmount(String(Math.max(nextBidCents / 100, Math.round(current + difference))));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError("");
    const amountCents = Math.round(Number(amount) * 100);
    if (!selected) {
      setFormError("Choose a country first.");
      return;
    }
    if (!url.trim()) {
      setFormError("Enter your product URL.");
      return;
    }
    if (biddingClosed) {
      setFormError("Bidding is closed because the board reached the payment processor’s checkout ceiling.");
      return;
    }
    if (!Number.isSafeInteger(amountCents) || amountCents < nextBidCents || amountCents % 100 !== 0) {
      setFormError(
        highestBidCents > 0
          ? `Bid at least ${money(nextBidCents)} — $3 above the current high of ${money(highestBidCents)}.`
          : `Enter a whole-dollar bid of at least ${money(nextBidCents)}.`,
      );
      return;
    }
    onClaim(selected, { url: url.trim(), amountCents });
  };

  const claimRank = ({ slot }: BoardEntry) => {
    selectCountry(slot, true);
    setTargetRank(1);
    setAmount(String(displayedBidCents / 100));
    document.querySelector("#earth-bid")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    window.setTimeout(() => urlInputRef.current?.focus({ preventScroll: true }), 350);
  };

  const selectFromGlobe = (slot: Slot) => {
    selectCountry(slot, true);
    document.querySelector("#earth-bid")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const renderEntry = (item: BoardEntry) => {
    const { entry, slot } = item;
    const globalRank = rankByEntry.get(`${slot.id}:${entry.id}`) ?? 0;
    const website = link(entry.url);
    const icon = favicon(entry.url);
    return (
      <article className={`earth-rank-card${globalRank <= 3 ? " earth-rank-card-top" : ""}`} id={`ranking-entry-${entry.id}`} key={`${slot.id}-${entry.id}`}>
        <span className="earth-rank-number">#{globalRank}</span>
        <a
          className="earth-rank-icon"
          href={outboundLink(entry.url, `global_rank_${globalRank}`)}
          target="_blank"
          rel="sponsored noopener noreferrer"
          aria-label={`Open ${entry.name}`}
        >
          <span aria-hidden="true">{entry.name.slice(0, 1).toUpperCase()}</span>
          {icon && <img src={icon} alt="" onError={(event) => event.currentTarget.remove()} />}
        </a>
        <div className="earth-rank-copy">
          <a
            className="earth-rank-name"
            href={outboundLink(entry.url, `global_rank_${globalRank}_name`)}
            target="_blank"
            rel="sponsored noopener noreferrer"
          >
            {entry.name}
          </a>
          {entry.tagline && <p>{entry.tagline}</p>}
          <div className="earth-rank-meta">
            <time dateTime={entry.createdAt}>{timeAgo(entry.createdAt)}</time>
            <span>·</span>
            <button type="button" onClick={() => { selectCountry(slot, true); }}>{slot.label}</button>
            {website && <span className="earth-rank-host">· {website.hostname.replace(/^www\./, "")}</span>}
          </div>
        </div>
        <strong className="earth-rank-amount">{money(entry.amountCents)}</strong>
        <button className="earth-rank-claim" type="button" onClick={() => claimRank(item)} disabled={biddingClosed}>
          {biddingClosed ? "bidding closed" : `claim #1 for ${money(nextBidCents)}`}
        </button>
      </article>
    );
  };

  return (
    <main className={`earth-site${dark ? " earth-dark" : ""}`} id="earth-top">
      <header className="earth-header">
        <a className="earth-logo" href="#earth-top" aria-label="outbid.earth home">
          <span className="earth-logo-mark" aria-hidden="true"><i /><i /><i /><b /></span>
          <strong>outbid.earth</strong>
        </a>
        <nav aria-label="Main">
          <a href="#leaderboard">Leaderboard</a>
          <a href="#countries">Countries</a>
          <a href="/rules">About</a>
          <a href="/rules">Rules</a>
        </nav>
        <button className="earth-theme-toggle" type="button" onClick={() => setDark((value) => !value)} aria-label={`Switch to ${dark ? "light" : "dark"} mode`}>
          {dark ? "☼" : "☾"}
        </button>
      </header>

      <section className="earth-bid-hero" id="earth-bid" aria-labelledby="earth-bid-title">
        <div className="earth-live-proof" aria-label="Live audience and leaderboard totals">
          <TrafficProof />
          <span>· <b>{active.length}</b> countries claimed</span>
          <span>· {globalEntries.length.toLocaleString("en-US")} websites on the board</span>
          <span>· {money(state.grossCents)} in bids</span>
        </div>

        <h1 id="earth-bid-title">
          <span>{biddingClosed ? "Board closed at" : `Claim #${targetRank} for`}</span>
          <button
            type="button"
            onClick={() => changeAmount(-1)}
            aria-label="Decrease bid by one dollar"
            disabled={biddingClosed || Number(amount) <= nextBidCents / 100}
          >−</button>
          <label>
            <span className="sr-only">Amount in dollars</span>
            <i>$</i>
            <input
              value={amount}
              onChange={(event) => setAmount(event.target.value.replace(/[^0-9]/g, ""))}
              aria-label="Amount in dollars"
              inputMode="numeric"
              style={{ width: `${Math.max(1, amount.length)}ch` }}
              disabled={biddingClosed}
            />
          </label>
          <button type="button" onClick={() => changeAmount(1)} aria-label="Increase bid by one dollar" disabled={biddingClosed}>+</button>
        </h1>

        <p className="earth-bid-explainer">
          {biddingClosed ? (
            <><strong>Bidding is closed.</strong>{" "}The board reached the payment processor’s checkout ceiling.</>
          ) : highestBidCents > 0 ? (
            <><strong>The current high is {money(highestBidCents)}.</strong>{" "}Bid at least {money(nextBidCents)} — $3 more — to take #1.</>
          ) : (
            <><strong>New spots start at {money(nextBidCents)}.</strong>{" "}Take the first place on the board.</>
          )}
        </p>

        <form className="earth-bid-form" onSubmit={submit}>
          <label className="earth-url-field">
            <span aria-hidden="true">◎</span>
            <input
              ref={urlInputRef}
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              maxLength={500}
              inputMode="url"
              autoComplete="url"
              placeholder="Your product URL"
              aria-label="Your product URL"
            />
          </label>
          <label className="earth-country-select">
            <span className="sr-only">Country</span>
            <select value={selectedId} onChange={(event) => selectCountry(slots.find((slot) => slot.id === event.target.value))} aria-label="Country">
              <option value="">Choose a country</option>
              {countryOptions.map((slot) => <option value={slot.id} key={slot.id}>{slot.label}</option>)}
            </select>
            <span aria-hidden="true">⌄</span>
          </label>
          <button className="earth-outbid-button" type="submit" disabled={biddingClosed || !state.stripeEnabled || !url.trim() || !selectedId}>
            {biddingClosed ? "Bidding closed" : state.stripeEnabled ? "Outbid" : "Opening soon"}
          </button>
        </form>
        {formError && <p className="earth-form-error" role="alert">{formError}</p>}
        <p className="earth-returning-note">Already on the list? Enter the same URL and up your bid.</p>
      </section>

      <nav className="earth-country-filters" aria-label="Ranking countries">
        <button className={filterId === "all" ? "is-active" : ""} type="button" onClick={() => setFilterId("all")}><span>▦</span> All</button>
        {active.map((slot) => (
          <button className={filterId === slot.id ? "is-active" : ""} type="button" onClick={() => { setFilterId(slot.id); selectCountry(slot); }} key={slot.id}>
            <span>◎</span> {slot.label}
          </button>
        ))}
      </nav>

      <section className="earth-leaderboard" id="leaderboard" aria-label="Website leaderboard">
        {displayedEntries.slice(0, 3).map(renderEntry)}

        {!!recentEntries.length && (
          <aside className="earth-activity" aria-labelledby="earth-activity-title">
            <h2 id="earth-activity-title"><i aria-hidden="true" />Latest activity</h2>
            <ol>
              {recentEntries.map(({ entry, slot }) => {
                const globalRank = rankByEntry.get(`${slot.id}:${entry.id}`) ?? 0;
                return (
                  <li key={`activity-${slot.id}-${entry.id}`}>
                    <button type="button" onClick={() => claimRank({ entry, slot })}>
                      <strong>{entry.name}</strong>
                      <span>at #{globalRank} · {money(entry.amountCents)}</span>
                      <time dateTime={entry.createdAt}>{timeAgo(entry.createdAt)}</time>
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>
        )}

        {displayedEntries.slice(3).map(renderEntry)}
        {!displayedEntries.length && (
          <div className="earth-empty-board">
            <strong>No websites here yet.</strong>
            <span>Choose this country above and take #1 for {money(nextBidCents)}.</span>
          </div>
        )}
      </section>

      <section className="earth-made">
        <p>This <a href="/rules">simple side project</a> made</p>
        <strong>{money(state.grossCents)}</strong>
        <p>since launch</p>
      </section>

      <section className="earth-map-section" id="countries" aria-labelledby="earth-map-title">
        <div className="earth-map-heading">
          <div>
            <span>Explore countries</span>
            <h2 id="earth-map-title">Pick a place on the globe</h2>
          </div>
          <p>Click a country to select it in the bid form.</p>
        </div>
        <div className="earth-globe-shell">
          <EarthGlobe slots={slots} selectedSlot={selected} onSelect={selectFromGlobe} />
        </div>
      </section>

      <footer className="earth-footer">
        <p>Built on Earth · <a href="/rules">About</a> · <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a></p>
      </footer>
    </main>
  );
}
